import { Box, Button, IconButton, Typography, Chip, alpha, Paper, useTheme } from "@mui/material";
import { genericFormProps } from "../signup.types";
import { useCallback, useContext, useState, useRef, useEffect } from "react";
import { TierContext } from "../../../context/TierContext";
import { Upload, Mic, CheckCircle, FiberManualRecord, Stop, PlayArrow, Pause } from "@mui/icons-material";
import { verifyFileSize } from "../helper";
import DeleteIcon from '../../../svg/DeleteIcon3';
import { toast } from "react-toastify";
import { getDownloadURL, ref } from "firebase/storage";
import { firestorage } from "../../../utils/firebase";

// const enableDevTools = process.env.REACT_APP_ENABLE_DEVTOOLS === 'true';

const VoiceUploadForm: React.FC<genericFormProps> = (props) => {
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    formSubmitting,
    isValid,
    dirty,
    showSteps
  } = props;

  const theme = useTheme();
  const { checkPermission } = useContext(TierContext);

  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const [playingIndex, setPlayingIndex] = useState<number | null>(null);
  const audioRefs = useRef<{ [key: number]: HTMLAudioElement }>({});
  const [existingVoiceUrl, setExistingVoiceUrl] = useState<string | null>(null);

  // Fetch download URL for existing voice sample
  useEffect(() => {
    const fetchExistingVoiceUrl = async () => {
      const existingVoiceSample = values?.existingVoiceSample;
      if (existingVoiceSample && typeof existingVoiceSample === 'string') {
        try {
          const storageRef = ref(firestorage, existingVoiceSample);
          const url = await getDownloadURL(storageRef);
          setExistingVoiceUrl(url);
        } catch (error) {
          console.error('Error fetching voice sample URL:', error);
          toast.error('Failed to load existing voice sample');
        }
      } else {
        setExistingVoiceUrl(null);
      }
    };

    fetchExistingVoiceUrl();
  }, [values?.existingVoiceSample]);


  const togglePlayPause = useCallback((index: number, audioUrl: string) => {
    // Stop any currently playing audio
    if (playingIndex !== null && playingIndex !== index) {
      audioRefs.current[playingIndex]?.pause();
    }

    if (!audioRefs.current[index]) {
      audioRefs.current[index] = new Audio(audioUrl);
      audioRefs.current[index].onended = () => setPlayingIndex(null);
    }

    const audio = audioRefs.current[index];

    if (playingIndex === index) {
      audio.pause();
      setPlayingIndex(null);
    } else {
      audio.play();
      setPlayingIndex(index);
    }
  }, [playingIndex]);

  // Cleanup audio refs on unmount
  useEffect(() => {
    const currentAudioRefs = audioRefs.current;
    return () => {
      Object.values(currentAudioRefs).forEach(audio => {
        audio.pause();
        audio.src = '';
      });
    };
  }, []);

  const startRecording = async () => {
    if (!checkPermission("update:voice")) {
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const audioFile = new File([audioBlob], `voice-recording-${Date.now()}.webm`, { type: 'audio/webm' });

        // Validate file size
        const fileSize = Math.round(audioFile.size / 1024);
        if (!verifyFileSize(fileSize, 50000)) {
          toast.error('Recording size must be less than 50MB');
          stream.getTracks().forEach(track => track.stop());
          return;
        }

        // Replace any existing voice sample
        setFieldValue('voiceSample', audioFile);
        stream.getTracks().forEach(track => track.stop());
        toast.success('Recording saved');
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

      toast.success('Recording started');
    } catch (error) {
      console.error('Error accessing microphone:', error);
      toast.error('Unable to access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      toast.success('Recording stopped');
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const renderVoiceListItem = useCallback(
    (
      document: any,
      index: number,
      onDeleteHandler: () => void,
      stripName: boolean = false,
      isExisting: boolean = false,
    ) => {
      let docName = (typeof document === 'string') ? document : document.name;
      if (stripName) {
        docName = docName.replace(/^.*[\\/]/, '');
      }

      // Get file size if available
      const fileSize = document?.size ? (document.size / (1024 * 1024)).toFixed(2) + ' MB' : null;

      // Create audio URL for this specific item
      let audioUrl: string | null = null;
      if (document instanceof File) {
        audioUrl = URL.createObjectURL(document);
      } else if (typeof document === 'string' && isExisting && existingVoiceUrl) {
        // Use the fetched download URL for existing voice samples
        audioUrl = existingVoiceUrl;
      }

      const isCurrentlyPlaying = playingIndex === index;

      return (
        <Box
          key={`voicesample-${index}`}
          display='flex'
          flexDirection='column'
          borderBottom={1}
          borderColor='divider'
          px={2.5}
          py={1.5}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1 }}>
              <Mic sx={{ fontSize: '20px', color: theme.palette.primary.main }} />

              <Box sx={{ flex: 1 }}>
                <Typography
                  variant='body2'
                  sx={{
                    wordBreak: 'break-word',
                    fontWeight: 500,
                  }}
                >
                  {docName}
                </Typography>

                {fileSize && (
                  <Typography variant='caption' color='text.secondary'>
                    {fileSize}
                  </Typography>
                )}

                {isExisting ? (
                  <Box display='flex' alignItems='center' gap={0.5} mt={0.5}>
                    <CheckCircle sx={{ fontSize: 14, color: 'success.main' }} />
                    <Typography variant='caption' color='success.main'>
                      Currently active
                    </Typography>
                  </Box>
                ) : (
                  <Chip
                    label="Ready to upload"
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.7rem',
                      mt: 0.5,
                      backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                      color: 'primary.main',
                      fontWeight: 500,
                    }}
                  />
                )}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {/* Play/Pause Button */}
              {audioUrl && (
                <IconButton
                  size='small'
                  sx={{
                    width: '36px',
                    height: '36px',
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.2),
                    },
                  }}
                  onClick={() => togglePlayPause(index, audioUrl!)}
                >
                  {isCurrentlyPlaying ? (
                    <Pause sx={{ color: theme.palette.primary.main, fontSize: '20px' }} />
                  ) : (
                    <PlayArrow sx={{ color: theme.palette.primary.main, fontSize: '20px' }} />
                  )}
                </IconButton>
              )}

              {/* Delete Button */}
              <IconButton
                id={`button-delete-${docName}-${index}`}
                size='small'
                sx={{
                  width: '30px',
                  height: '30px',
                }}
                onClick={onDeleteHandler}
              >
                <DeleteIcon stroke={theme.palette["nav"].iconactive} />
              </IconButton>
            </Box>
          </Box>
        </Box>
      )
    }, [theme, playingIndex, togglePlayPause, existingVoiceUrl]);

  const voiceSample = values?.voiceSample;
  const existingVoiceSample = values?.existingVoiceSample;
  const currentSample = voiceSample || existingVoiceSample;
  const hasVoiceSample = !!currentSample;

  const handleFileChange = (file: File) => {
    if (!checkPermission("update:voice")) {
      return;
    }

    // max voice sample file size is 50MB
    const fileSize = Math.round(file.size / 1024);
    if (!verifyFileSize(fileSize, 50000)) {
      toast.error('File size must be less than 50MB');
      return;
    }

    // Validate audio file type
    const audioTypes = ['audio/mpeg', 'audio/wav', 'audio/mp3', 'audio/ogg', 'audio/webm', 'audio/m4a'];
    if (!audioTypes.includes(file.type) && !file.name.match(/\.(mp3|wav|ogg|webm|m4a)$/i)) {
      toast.error('Please upload a valid audio file (MP3, WAV, OGG, M4A)');
      return;
    }

    // Replace any existing voice sample
    setFieldValue('voiceSample', file);
    toast.success('Voice sample added');
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileChange(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      {/* Page Title */}
      <Box>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            mb: 0.5,
          }}
        >
          Upload Voice Sample
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
          }}
        >
          Train your AI persona's voice with your audio sample
        </Typography>
            <Typography
              sx={{
                  color: 'primary.secondary',
                  fontSize: 16,
                  fontFamily: '"Merriweather Sans", sans-serif',
                  fontWeight: 600,
                  lineHeight: '20.8px',
                  letterSpacing: '0.15px',
                  mb: 2,
                  mt: 2,
                }}
            >
               *Optional
            </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 4,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.background["container-2"]
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'start',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Mic sx={{ color: theme.palette.primary.main }} />
            <Typography variant='h7'>Add Voice Sample</Typography>
          </Box>
          <Typography variant='body2'>
            Upload a 1-2 minute audio sample or record live in MP3, WAV, or other standard audio format to personalize your AI's voice.
          </Typography>
        </Box>

        {/* Recording Tips */}
        <Box
          sx={{
            mb: 3,
            p: 2,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.warning.main, 0.1),
            border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
          }}
        >
          <Typography
            variant="caption"
            display="block"
            sx={{
              color: theme.palette.text.primary,
              fontWeight: 600,
              mb: 1,
            }}
          >
            Recording Tips:
          </Typography>
          <Typography
            variant="caption"
            display="block"
            sx={{
              color: theme.palette.text.secondary,
              lineHeight: 1.6,
            }}
          >
            • Find a quiet space with no background noise<br />
            • Speak clearly and naturally at a consistent pace<br />
            • Aim for 1-2 minutes of continuous speech<br />
            • Avoid mumbling, whispering, or shouting
          </Typography>
        </Box>

        {/* Side by Side Options */}
        <Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2,
              alignItems: 'stretch',
            }}
          >
            {/* Recording Option */}
            <Box
              sx={{
                p: 3,
                borderRadius: 3,
                border: '2px solid',
                borderColor: isRecording ? theme.palette.error.main : theme.palette.divider,
                bgcolor: isRecording
                  ? alpha(theme.palette.error.main, 0.05)
                  : theme.palette.mode === 'dark'
                    ? alpha(theme.palette.background.paper, 0.4)
                    : alpha(theme.palette.primary.main, 0.02),
                textAlign: 'center',
                transition: 'all 0.3s ease-in-out',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  bgcolor: isRecording
                    ? alpha(theme.palette.error.main, 0.1)
                    : alpha(theme.palette.primary.main, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                }}
              >
                <Mic sx={{ fontSize: 32, color: isRecording ? 'error.main' : 'primary.main' }} />
              </Box>

              {isRecording ? (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <FiberManualRecord sx={{ color: 'error.main', fontSize: 16 }} />
                    <Typography variant="h6" color="error.main" fontWeight={600}>
                      Recording: {formatTime(recordingTime)}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<Stop />}
                    onClick={stopRecording}
                    sx={{
                      px: 4,
                      py: 1.5,
                      borderRadius: 2,
                      fontWeight: 600,
                      boxShadow: theme.shadows[4],
                    }}
                  >
                    Stop Recording
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="contained"
                    startIcon={<Mic />}
                    onClick={startRecording}
                    disabled={formSubmitting}
                    sx={{
                      px: 4,
                      py: 1.5,
                      borderRadius: 2,
                      fontWeight: 600,
                      boxShadow: theme.shadows[4],
                    }}
                  >
                    Start Recording
                  </Button>
                </>
              )}
            </Box>

            {/* Upload Area with Drag & Drop */}
            <Box
              component="label"
              htmlFor="file-input-voiceSample"
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                p: 3,
                borderRadius: 3,
                border: '2px dashed',
                borderColor: theme.palette.divider,
                bgcolor: theme.palette.mode === 'dark'
                  ? alpha(theme.palette.background.paper, 0.4)
                  : alpha(theme.palette.primary.main, 0.02),
                textAlign: 'center',
                transition: 'all 0.3s ease-in-out',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                },
              }}
              onClick={(event) => {
                if (!checkPermission("update:voice")) {
                  event.preventDefault();
                  event.stopPropagation();
                }
              }}
            >
              <input
                type='file'
                accept='audio/*,.mp3,.wav,.ogg,.m4a'
                id='file-input-voiceSample'
                name='voiceSample'
                hidden
                disabled={formSubmitting}
                onChange={(event) => {
                  const file = event?.currentTarget?.files?.[0];
                  if (file) {
                    handleFileChange(file);
                  }
                }}
              />

              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                }}
              >
                <Upload sx={{ fontSize: 32, color: 'primary.main' }} />
              </Box>

              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  bgcolor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  fontWeight: 600,
                  boxShadow: theme.shadows[4],
                  transition: 'all 0.2s ease-in-out',
                  pointerEvents: 'none',
                  margin: '0 auto',
                  '&:hover': {
                    boxShadow: theme.shadows[8],
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <Upload sx={{ fontSize: 20 }} />
                Choose Audio File
              </Box>

              <Typography
                variant="body2"
                display="block"
                mt={1.5}
                sx={{
                  color: theme.palette.text.secondary,
                }}
              >
                or drag and drop
              </Typography>

              <Typography
                variant="caption"
                display="block"
                mt={1}
                sx={{
                  color: theme.palette.text.secondary,
                }}
              >
                MP3, WAV, OGG, M4A • Max 50MB
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Voice Sample Display Section */}
      {hasVoiceSample && (
        <Box mt={1}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: 'space-between',
              bgcolor: theme.palette.background["container-2"]
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Mic sx={{ color: theme.palette.primary.main }} />
              <Typography variant="h7">My Voice Sample</Typography>
            </Box>
            {voiceSample && (
              <Chip
                label="New"
                size="small"
                sx={{
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                  color: 'primary.main',
                }}
              />
            )}
          </Paper>

          <Box>
            {/* Render new voice sample */}
            {voiceSample && renderVoiceListItem(voiceSample, 0, () => {
              setFieldValue('voiceSample', null);
            }, false, false)}
            {/* Render existing voice sample */}
            {!voiceSample && existingVoiceSample && renderVoiceListItem(existingVoiceSample, 0, () => {
              setFieldValue('existingVoiceSample', '');
            }, true, true)}
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default VoiceUploadForm;
